"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const Lock_1 = __importDefault(require("@mui/icons-material/Lock"));
const material_1 = require("@mui/material");
const lexico_1 = __importDefault(require("@tradeshift/lexico"));
const axios_1 = require("axios");
const pino_1 = __importDefault(require("pino"));
const react_1 = require("react");
const react_router_1 = require("react-router");
const react_use_1 = require("react-use");
const strings_1 = require("../../../../../../../constants/strings");
const sessionQuery_1 = require("../../../../../../../queries/sessionQuery");
const routerUtils_1 = require("../../../../../../../router/routerUtils");
const phoneNumberUtils_1 = require("../../../../../../../utils/phoneNumberUtils");
const zustandStore_1 = require("../../../../../../../zustandStore");
const pageSpinner_1 = __importDefault(require("../../../../../../molecules/pageSpinner"));
const formWrapper_1 = __importDefault(require("../../../formWrapper"));
const useValidation_1 = require("../../../hooks/useValidation");
const formInput_1 = __importDefault(require("../../common/formInput"));
const termsAndConditionsFormFooter_1 = __importDefault(require("../../common/termsAndConditionsFormFooter"));
const validationSchema_1 = require("./validationSchema");
const lexico = new lexico_1.default();
const logger = (0, pino_1.default)();
// HSBC
const InviteRegisterForm = () => {
    const initialData = (0, react_router_1.useLoaderData)();
    const { data, isLoading, error } = initialData;
    const theme = (0, material_1.useTheme)();
    const navigate = (0, react_router_1.useNavigate)();
    const [formState, setFormState] = (0, react_1.useState)({
        firstName: data?.firstName || '',
        lastName: data?.lastName || '',
        username: data?.username || '',
        phoneDetails: data?.phoneDetails || {
            phoneCountryCode: '',
            phoneNumber: '',
            fullPhoneNumber: '',
        },
        tempPassword: '',
    });
    const { navigateToLockedAccountMessage } = (0, routerUtils_1.useSigninMessageNavigation)();
    const activateUserTemporaryPassword = (0, sessionQuery_1.useRequestActivationWithTemporaryPasswordQuery)();
    const { validateField, clearFieldError, fieldErrors, validateAllSchema } = (0, useValidation_1.useValidation)();
    const { setPageLoading, setCurrentStep, steps, setDialog, closeDialog, setStepsData } = (0, zustandStore_1.useZustand)(state => ({
        ...state,
    }));
    const { userId, activationToken } = (0, react_router_1.useParams)();
    const [activationCodeError, setActivationCodeError] = (0, react_1.useState)(null);
    const maskedPhoneNumber = (0, phoneNumberUtils_1.maskPhoneNumber)(formState.phoneDetails.phoneCountryCode, formState.phoneDetails.phoneNumber);
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormState(prev => {
            const updatedState = { ...prev, [name]: value };
            clearFieldError(name);
            return updatedState;
        });
    };
    const handleBlur = (e) => {
        if (!e || !e.target) {
            return;
        }
        const { name = '' } = e.target;
        validateField(validationSchema_1.inviteRegisterSchema, name, formState);
    };
    const handleSubmit = (e) => {
        e.preventDefault();
        validateAllSchema(validationSchema_1.inviteRegisterSchema, formState);
        if (!data) {
            logger.error('data is missing');
            return;
        }
        setPageLoading(true);
        const options = {
            activationToken: activationToken || '',
            userId: userId || '',
            temporaryPassword: formState.tempPassword,
            firstName: formState.firstName,
            lastName: formState.lastName,
        };
        activateUserTemporaryPassword.mutate(options, {
            onSuccess: async () => {
                setActivationCodeError(null);
                setCurrentStep(steps.currentStep + 1);
                setStepsData({
                    tempPassword: formState.tempPassword,
                    user: {
                        firstName: formState.firstName,
                        lastName: formState.lastName,
                    },
                });
                setFormState({
                    firstName: '',
                    lastName: '',
                    username: '',
                    phoneDetails: {
                        phoneCountryCode: '',
                        phoneNumber: '',
                        fullPhoneNumber: '',
                    },
                    tempPassword: '',
                });
            },
            onError: error => {
                setFormState(prevState => ({
                    ...prevState,
                    tempPassword: '',
                }));
                if ((0, axios_1.isAxiosError)(error) &&
                    (error.response?.status === 400 ||
                        error.response?.data === 'Invalid temporary password or activation token provided')) {
                    setActivationCodeError((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: ["You've entered the wrong activation code, ", (0, jsx_runtime_1.jsx)("br", {}), "please try again."] }));
                    // Add a custom style to the FormInput for this specific error
                    const tempPasswordInput = document.querySelector('input[name="tempPassword"]');
                    if (tempPasswordInput) {
                        const errorElement = tempPasswordInput.parentElement?.querySelector('.MuiFormHelperText-root');
                        if (errorElement) {
                            errorElement.innerHTML = "You've entered the wrong activation code,<br>please try again.";
                        }
                    }
                }
                else if ((0, axios_1.isAxiosError)(error) &&
                    error.response?.status === 403 &&
                    error.response?.data?.errorCode === 'LOCKED_ACCOUNT') {
                    navigateToLockedAccountMessage('activation');
                    return;
                }
                else {
                    setDialog({
                        type: strings_1.notificationStrings.WARNING,
                        message: lexico.trc('Warning message', 'Invalid activation code. Please try again.'),
                        open: true,
                        actionText: 'TRY AGAIN',
                        showCancel: false,
                        onAccept: closeDialog,
                    });
                }
                setFormState(prevState => ({
                    ...prevState,
                    tempPassword: '',
                }));
            },
            onSettled: () => {
                setPageLoading(false);
            },
        });
    };
    (0, react_use_1.useEffectOnce)(() => {
        if (!data) {
            return;
        }
        if (data.activationCodeAttemptsExceeded) {
            navigateToLockedAccountMessage('activation');
            return;
        }
    });
    (0, react_use_1.useEffectOnce)(() => {
        if (!error) {
            return;
        }
        void navigate('/signin/message', {
            state: {
                heading: lexico.tr('Sorry, we are having technical difficulties. Please try again later.'),
                message: lexico.trc('Error message', 'The link to activate your account is invalid or has expired. Please check with the person who invited you to get another invitation.'),
            },
        });
    });
    return ((0, jsx_runtime_1.jsxs)(formWrapper_1.default, { formName: "Accept invitation to join", onSubmit: handleSubmit, submitButton: {
            label: 'Register',
            isDisabled: !validationSchema_1.inviteRegisterSchema.safeParse(formState).success,
        }, isFormPending: activateUserTemporaryPassword.isPending, formFooter: (0, jsx_runtime_1.jsx)(termsAndConditionsFormFooter_1.default, {}), children: [(0, jsx_runtime_1.jsx)(formInput_1.default, { label: "Email", name: "email", type: "email", value: formState.username, error: fieldErrors['username'], onBlur: handleBlur, readOnly: true, endAdornment: (0, jsx_runtime_1.jsx)(material_1.InputAdornment, { position: "end", children: (0, jsx_runtime_1.jsx)(Lock_1.default, { color: "disabled" }) }), onChange: handleInputChange }), (0, jsx_runtime_1.jsx)(formInput_1.default, { label: "Phone number", type: "tel", name: "phoneNumber", value: maskedPhoneNumber, error: fieldErrors['phoneDetails.fullPhoneNumber'], onChange: handleInputChange, onBlur: handleBlur, readOnly: true, endAdornment: (0, jsx_runtime_1.jsx)(material_1.InputAdornment, { position: "end", children: (0, jsx_runtime_1.jsx)(Lock_1.default, { color: "disabled" }) }), placeholder: "(+45) 12 34 56 78" }), (0, jsx_runtime_1.jsx)(material_1.Alert, { severity: "info", sx: {
                    marginBottom: '16px',
                    color: theme.palette.tsGoColor.black,
                    borderRadius: '4px',
                    border: '1px solid #93C5FD',
                    margin: '0 10px',
                    padding: '5px 10px',
                    fontSize: theme.typography.pxToRem(theme.typography.tsFontSize.mini),
                }, children: "Please ensure that the provided phone number is yours. If it is not your number, please reach out to your team owner before proceeding." }), (0, jsx_runtime_1.jsx)(formInput_1.default, { label: "First Name", name: "firstName", value: formState.firstName, error: fieldErrors['firstName'], onChange: handleInputChange, onBlur: handleBlur }), (0, jsx_runtime_1.jsx)(formInput_1.default, { label: "Last Name", name: "lastName", value: formState.lastName, error: fieldErrors['lastName'], onChange: handleInputChange, onBlur: handleBlur }), (0, jsx_runtime_1.jsx)(formInput_1.default, { label: "Activation Code", name: "tempPassword", type: "password", value: formState.tempPassword, error: activationCodeError || fieldErrors['tempPassword'], onChange: handleInputChange, onBlur: handleBlur }), isLoading ? (0, jsx_runtime_1.jsx)(pageSpinner_1.default, { isLoading: true }) : null] }));
};
exports.default = InviteRegisterForm;
